import {
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';

import { MoreFilters } from '@/components/MoreFilters';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { SearchTips } from '@/imports/ui/components/algolia/SearchTips';
import {
  Strawberry,
  StrawberryAside,
  StrawberryMain,
} from '@/layouts/Strawberry';

import { Hits } from './components/Hits';
import { MemberCompanyDirectorySelectedFilters } from './components/MemberCompanyDirectorySelectedFilters';
import { useMemberCompanyDirectorySearch } from './hooks';

export const MemberCompanies: React.FC = () => {
  const directory = useMemberCompanyDirectorySearch();

  if (directory.isInitialLoading) {
    return (
      <Strawberry>
        <StrawberryMain>
          <Loading />
        </StrawberryMain>
        <StrawberryAside />
      </Strawberry>
    );
  }

  if (directory.memberCompanyDirectorySearch.error) {
    return (
      <Strawberry>
        <StrawberryMain>
          <UnexpectedError />
        </StrawberryMain>
        <StrawberryAside />
      </Strawberry>
    );
  }

  return (
    <Strawberry>
      <StrawberryMain>
        <Stack spacing="4">
          <Card>
            <Stack spacing="0" divider={<Divider />}>
              <Flex py="4" pl="4">
                <InputGroup flex="1">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="primary.500" />
                  </InputLeftElement>
                  <Input
                    variant="outline"
                    borderRadius="md"
                    placeholder="Search Organizations"
                    type="search"
                    value={directory.searchText}
                    onChange={(event) =>
                      directory.setSearchText(event.target.value)
                    }
                  />
                </InputGroup>
                <SearchTips ownerKind={EAttributeOwnerKind.ORGANIZATION} />
              </Flex>

              {(directory.selectedFilters.length > 0 ||
                directory.unselectedFilters.length > 0) && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" p="4">
                  <MemberCompanyDirectorySelectedFilters
                    selectedFilters={directory.selectedFilters}
                    unselectFilter={directory.unselectFilter}
                    selectFilter={directory.selectFilter}
                    freeTextValues={directory.freeTextValues}
                    onFreeTextValueChange={directory.setFreeTextValue}
                  />

                  <Flex direction="column">
                    <Spacer />
                    <MoreFilters
                      unselectedFilters={directory.unselectedFilters}
                      selectedFilters={directory.selectedFilters}
                      selectFilter={directory.selectFilter}
                      unselectFilter={directory.unselectFilter}
                    />
                  </Flex>
                </SimpleGrid>
              )}
            </Stack>
          </Card>

          <Card px="4" py="6" pos="relative">
            <Text color="gray.700">
              Showing {directory.totalCount.toLocaleString()} Result
              {directory.totalCount === 1 ? '' : 's'}
            </Text>
            <Hits
              companies={directory.results}
              loading={directory.isLoading && directory.results.length === 0}
              hasMore={directory.hasMore}
              isLoadingMore={directory.isLoadingMore}
              onLoadMore={directory.loadMore}
            />
          </Card>
        </Stack>
      </StrawberryMain>
      <StrawberryAside />
    </Strawberry>
  );
};
